import React, { useEffect, useState } from "react";
import { BACKEND_URL } from "@/lib/backendUrl";
import axios from "axios";
import { useMixer } from "@/context/MixerContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Circle, Square, Link2, Loader2, Share2 } from "lucide-react";

const API = `${BACKEND_URL}/api`;

/**
 * Records a set and publishes it as a shareable public link with the tracklist.
 * Replaces the previous Record Set button — while recording, it captures the
 * tracklist events (deck + track + elapsed seconds). When user hits Stop, opens
 * a publish modal.
 */
export default function RecordAndPublish({ decks }) {
  const { engine, isRecording, recElapsed, startRec, stopRec } = useMixer();
  const [tracklist, setTracklist] = useState([]);
  const [publishOpen, setPublishOpen] = useState(false);
  const [pendingBlob, setPendingBlob] = useState(null);
  const [setName, setSetName] = useState("");
  const [publishing, setPublishing] = useState(false);
  const [publishedUrl, setPublishedUrl] = useState(null);
  const [rightsOk, setRightsOk] = useState(false);
  const [copyrightHits, setCopyrightHits] = useState([]);
  const [scanRan, setScanRan] = useState(false);
  const [explainOpen, setExplainOpen] = useState(false);

  // Track "now-playing per deck" changes into the tracklist while recording
  const lastTracksRef = React.useRef({});
  useEffect(() => {
    if (!isRecording) { lastTracksRef.current = {}; return; }
    ["A", "B", "C", "D"].forEach(id => {
      const d = decks[id];
      const lastId = lastTracksRef.current[id];
      if (d.playing && d.track && d.track.id !== lastId) {
        lastTracksRef.current[id] = d.track.id;
        setTracklist(prev => [...prev, {
          deck_id: id,
          title: d.track.title,
          artist: d.track.artist,
          bpm: d.track.bpm || null,
          key: d.track.key || null,
          played_at: recElapsed,
        }]);
      }
    });
  }, [isRecording, decks, recElapsed]);

  const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  const start = async () => {
    setTracklist([]);
    setPublishedUrl(null);
    lastTracksRef.current = {};
    await startRec();
    // NOTE: no manual seeding here — the useEffect below picks up any currently
    // playing decks on its next tick and adds them exactly once. Seeding here
    // caused a race where the effect and this loop both appended, producing
    // duplicate tracklist rows.
  };

  const stopAndOpen = async () => {
    // Capture the blob via a custom stop that doesn't auto-download
    if (!engine || !engine.recorder || engine.recorder.state === "inactive") return;
    const blob = await new Promise(resolve => {
      engine.recorder.onstop = () => {
        const b = new Blob(engine.recChunks, { type: engine.recorder.mimeType });
        engine.recStartedAt = null;
        resolve(b);
      };
      engine.recorder.stop();
    });
    setPendingBlob(blob);
    setSetName(`Live Set — ${new Date().toLocaleString()}`);
    setPublishOpen(true);
    setRightsOk(false);
    setScanRan(false);
    setCopyrightHits([]);
    setExplainOpen(false);
    // Fire-and-forget copyright scan so the DJ sees a warning before publishing
    axios.post(`${API}/sets/scan-copyright`, { name: "scan", tracklist, rights_confirmed: true })
      .then(r => { setCopyrightHits(r.data.hits || []); setScanRan(true); })
      .catch(() => setScanRan(true));
    // Also trigger local download
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
    a.download = `neuraldeck-set-${ts}.webm`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
    // Reset isRecording state via a fake stop
    await stopRec().catch(() => {});
  };

  const publish = async () => {
    if (!pendingBlob) return;
    if (!rightsOk) { toast.error("Confirm you hold the rights first."); return; }
    setPublishing(true);
    try {
      // Convert blob to base64
      const b64 = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result.toString().split(",")[1]);
        r.onerror = () => reject(new Error("blob read failed"));
        r.readAsDataURL(pendingBlob);
      });
      if (b64.length > 14 * 1024 * 1024 * 4 / 3) {
        toast.error("Recording is too large to publish (>10 min). Downloaded locally instead.");
        setPublishing(false);
        return;
      }
      const r = await axios.post(`${API}/sets/publish`, {
        name: setName || "Untitled Set",
        tracklist,
        audio_base64: b64,
        audio_mime: pendingBlob.type,
        duration_seconds: recElapsed,
        rights_confirmed: true,
      });
      const shareUrl = `${window.location.origin}/set/${r.data.id}`;
      setPublishedUrl(shareUrl);
      toast.success("Set published.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Publish failed");
    } finally { setPublishing(false); }
  };

  const copyLink = () => {
    if (!publishedUrl) return;
    navigator.clipboard.writeText(publishedUrl);
    toast.success("Link copied to clipboard.");
  };

  const toggle = async () => { if (isRecording) await stopAndOpen(); else await start(); };

  return (
    <>
      <button
        data-testid="master-recording-button"
        onClick={toggle}
        className={`w-full py-2.5 rounded-lg font-black uppercase tracking-[0.2em] text-xs transition-all active:scale-95 flex items-center justify-center gap-2 ${
          isRecording
            ? "bg-[#EF4444] text-white shadow-[0_0_25px_rgba(239,68,68,0.5)] animate-pulse"
            : "bg-transparent border border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444] hover:text-white"
        }`}
      >
        {isRecording ? <><Square className="w-3.5 h-3.5 fill-current"/> Stop & Publish · {fmt(recElapsed)}</> : <><Circle className="w-3.5 h-3.5 fill-current"/> Record Set</>}
      </button>

      <Dialog open={publishOpen} onOpenChange={setPublishOpen}>
        <DialogContent className="bg-[#121620] border-[#2A354B] text-white sm:max-w-lg" data-testid="publish-set-modal">
          <DialogHeader>
            <DialogTitle className="font-display font-black text-2xl">
              {publishedUrl ? "Set Published" : "Publish Your Set"}
            </DialogTitle>
          </DialogHeader>
          {!publishedUrl ? (
            <div className="space-y-4 pt-2">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-[#94A3B8]">Set title</Label>
                <Input data-testid="publish-set-name" value={setName} onChange={e => setSetName(e.target.value)} className="bg-[#0B0E14] border-[#2A354B] text-white"/>
              </div>
              <div className="p-3 rounded-lg border border-[#1E2638] bg-[#0B0E14] max-h-40 overflow-y-auto space-y-1 text-xs">
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#94A3B8] mb-1">Tracklist ({tracklist.length})</div>
                {tracklist.length === 0 && <div className="text-[#64748B]">No tracks were played during this recording.</div>}
                {tracklist.map((t, i) => (
                  <div key={i} data-testid={`tracklist-row-${i}`} className="flex items-baseline gap-2">
                    <span className="font-mono text-[#94A3B8]">{fmt(t.played_at)}</span>
                    <span className="font-mono text-[#00F0FF]">[{t.deck_id}]</span>
                    <span className="truncate">{t.title} <span className="text-[#94A3B8]">· {t.artist}</span></span>
                  </div>
                ))}
              </div>
              {scanRan && copyrightHits.length > 0 && (
                <div className="p-3 rounded-lg border border-[#FFC107]/50 bg-[#FFC107]/5 space-y-2" data-testid="copyright-warning">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#FFC107] flex items-center gap-1">
                    ⚠ Copyright warning · {copyrightHits.length} track{copyrightHits.length === 1 ? "" : "s"} may be protected
                  </div>
                  <ul className="text-xs text-[#F8FAFC] space-y-0.5 list-disc list-inside">
                    {copyrightHits.slice(0, 4).map(h => (
                      <li key={h.index}><span className="font-bold">{h.title}</span> — {h.artist}</li>
                    ))}
                    {copyrightHits.length > 4 && <li className="text-[#94A3B8]">…and {copyrightHits.length - 4} more</li>}
                  </ul>
                  <button type="button" data-testid="copyright-explain-toggle" onClick={() => setExplainOpen(!explainOpen)}
                    className="text-[10px] font-mono uppercase tracking-widest text-[#FFC107] hover:text-white underline underline-offset-2">
                    {explainOpen ? "Hide details ▲" : "Why? Show details ▼"}
                  </button>
                  {explainOpen && (
                    <div className="pt-2 border-t border-[#FFC107]/30 space-y-3" data-testid="copyright-explain-drawer">
                      <div className="space-y-1.5">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-[#FFC107]">// Per-track reasons</div>
                        <ul className="text-[11px] text-[#F8FAFC] space-y-1">
                          {copyrightHits.map(h => (
                            <li key={h.index} className="flex flex-col">
                              <span className="font-bold">{h.title} — {h.artist}</span>
                              <span className="text-[#94A3B8] italic">↳ {h.reason}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="space-y-1.5">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-[#FFC107]">// Get a licence or replace the track</div>
                        <div className="grid grid-cols-3 gap-1.5">
                          <a href="https://www.epidemicsound.com/" target="_blank" rel="noopener noreferrer" data-testid="copyright-license-epidemic"
                            className="text-[10px] font-mono uppercase tracking-widest text-center px-2 py-1.5 rounded border border-[#FFC107]/40 hover:border-[#FFC107] hover:bg-[#FFC107]/10 text-[#FFC107] transition-colors">
                            Epidemic Sound
                          </a>
                          <a href="https://uppbeat.io/" target="_blank" rel="noopener noreferrer" data-testid="copyright-license-uppbeat"
                            className="text-[10px] font-mono uppercase tracking-widest text-center px-2 py-1.5 rounded border border-[#FFC107]/40 hover:border-[#FFC107] hover:bg-[#FFC107]/10 text-[#FFC107] transition-colors">
                            Uppbeat
                          </a>
                          <a href="https://artlist.io/" target="_blank" rel="noopener noreferrer" data-testid="copyright-license-artlist"
                            className="text-[10px] font-mono uppercase tracking-widest text-center px-2 py-1.5 rounded border border-[#FFC107]/40 hover:border-[#FFC107] hover:bg-[#FFC107]/10 text-[#FFC107] transition-colors">
                            Artlist
                          </a>
                        </div>
                        <div className="text-[10px] text-[#94A3B8] italic pt-1">
                          Or record with your own compositions / royalty-free packs. If you have a Content ID licence for a flagged track (e.g. via your label), publishing is fine — just remember platforms may still flag it downstream.
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="text-[10px] text-[#94A3B8] italic pt-1">Only publish sets you have permission to distribute (own material, royalty-free, or licensed).</div>
                </div>
              )}
              <label className="flex items-start gap-3 p-3 rounded-lg border border-[#2A354B] bg-[#0B0E14] cursor-pointer hover:border-[#00F0FF]" data-testid="rights-confirm-label">
                <input type="checkbox" checked={rightsOk} onChange={e => setRightsOk(e.target.checked)}
                  data-testid="rights-confirm-checkbox"
                  className="mt-1 w-4 h-4 accent-[#00F0FF]"/>
                <span className="text-xs text-[#F8FAFC] leading-relaxed">
                  I confirm I hold the rights or license to publish every track in this set, or the material is royalty-free / my own composition. I take responsibility for any DMCA takedown request.
                </span>
              </label>
              <Button data-testid="publish-set-confirm" onClick={publish} disabled={publishing || !pendingBlob || !rightsOk}
                className={`w-full font-black uppercase tracking-widest transition-colors ${
                  rightsOk
                    ? "bg-[#00F0FF] text-black hover:bg-[#33F3FF]"
                    : "bg-[#1E2638] text-[#64748B] cursor-not-allowed border border-[#2A354B]"
                }`}>
                {publishing ? <Loader2 className="w-4 h-4 animate-spin"/> : rightsOk ? <><Share2 className="w-4 h-4 mr-2"/> Publish & Get Link</> : <>Confirm rights above to enable</>}
              </Button>
              <div className="text-[11px] text-center text-[#94A3B8]">Also downloaded locally as .webm</div>
            </div>
          ) : (
            <div className="space-y-4 pt-2">
              <div className="text-sm text-[#94A3B8]">Anyone with this link can listen to your set with the tracklist.</div>
              <div className="flex items-center gap-2 p-3 rounded-lg border border-[#00F0FF]/40 bg-[#00F0FF]/5 font-mono text-xs break-all" data-testid="published-set-url">
                <Link2 className="w-4 h-4 flex-shrink-0 text-[#00F0FF]"/>
                <span className="flex-1">{publishedUrl}</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button data-testid="published-copy-link" onClick={copyLink} className="bg-[#00F0FF] text-black hover:bg-[#33F3FF] font-black uppercase tracking-widest">Copy Link</Button>
                <Button data-testid="published-open" onClick={() => window.open(publishedUrl, "_blank")} variant="outline" className="border-[#2A354B] bg-transparent text-white hover:border-[#00F0FF] font-black uppercase tracking-widest">Open</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
